import { useCallback, useMemo, useState } from 'react';
import {
  Button,
  ButtonSizes,
  ButtonVariants,
  Form,
  FormSizes,
  FormValidateOn,
  useFormApi,
  useFormState,
} from '@carboledger/ui';
import { Plus } from '@carboledger/ui/icons';
import { ConfirmDialog } from '@/components/feedback';
import { useTranslations } from '@/hooks';
import { isFormDirty } from '@/lib/utils';
import {
  MAX_SHIPPING_SITES,
  MIN_SHIPPING_SITES,
  SITE_KEYS_FIELD,
  emptySiteValues,
  nextSiteKey,
} from '../constants/customers.constants';
import { hasRequiredSites, hasUniqueSiteIds, siteKeysOf } from '../utils/customersUtils';
import { ShippingSiteCard } from './ShippingSiteCard';
import styles from './CustomerSitesForm.module.scss';

/**
 * Props:
 *   existingIds  the customer's saved site ids no card may reuse
 *   isEdit       one saved site: no add/remove, and its id stays locked
 */
const ShippingSiteCards = ({ existingIds, isEdit }) => {
  const t = useTranslations();
  const { values } = useFormState();
  const { setValue } = useFormApi();

  const siteKeys = siteKeysOf(values);

  const addSite = useCallback(() => {
    const siteKey = nextSiteKey(siteKeys);
    Object.entries(emptySiteValues(siteKey)).forEach(([name, value]) => setValue(name, value));
    setValue(SITE_KEYS_FIELD, [...siteKeys, siteKey]);
  }, [siteKeys, setValue]);

  const removeSite = useCallback(
    (siteKey) =>
      setValue(
        SITE_KEYS_FIELD,
        siteKeys.filter((key) => key !== siteKey),
      ),
    [siteKeys, setValue],
  );

  const addIcon = useMemo(() => <Plus size={16} />, []);

  return (
    <>
      {siteKeys.map((siteKey, index) => (
        <ShippingSiteCard
          key={siteKey}
          siteKey={siteKey}
          heading={t('SHIPPING_SITE_CARD_HEADING', { index: index + 1 })}
          canRemove={!isEdit && siteKeys.length > MIN_SHIPPING_SITES}
          onRemove={removeSite}
          existingIds={existingIds}
          isEdit={isEdit}
        />
      ))}

      {isEdit ? null : (
        // One column of a 2-up row — same width as a single address line above.
        <Form.Row columns={2}>
          <Button
            variant={ButtonVariants.secondary}
            size={ButtonSizes.sm}
            leftIcon={addIcon}
            disabled={siteKeys.length >= MAX_SHIPPING_SITES}
            onClick={addSite}
          >
            {t('SHIPPING_SITE_ADD_MORE')}
          </Button>
        </Form.Row>
      )}
    </>
  );
};

/**
 * Props:
 *   title          the top bar's heading
 *   initialValues  the sites form values
 *   existingIds    the customer's saved site ids no card may reuse
 *   onSubmit       (values) => Promise
 *   onClose        leaves the page
 *   isSubmitting   locks Save while the request is in flight
 *   isEdit         one saved site: Save waits for a real change
 */
export const CustomerSitesForm = ({
  title,
  initialValues,
  existingIds,
  onSubmit,
  onClose,
  isSubmitting = false,
  isEdit = false,
}) => {
  const t = useTranslations();

  const [confirmingClose, setConfirmingClose] = useState(false);
  const askBeforeClosing = useCallback(() => setConfirmingClose(true), []);
  const keepEditing = useCallback(() => setConfirmingClose(false), []);

  return (
    <Form
      validateOn={FormValidateOn.change}
      size={FormSizes.large}
      initialValues={initialValues}
      onSubmit={onSubmit}
      className={styles.form}
    >
      {({ values, isValid }) => {
        const isDirty = isFormDirty(values, initialValues);
        const requestClose = isDirty ? askBeforeClosing : onClose;
        // Edit saves only a real change; add saves only complete, unique sites.
        const canSave =
          isValid &&
          hasRequiredSites(values) &&
          hasUniqueSiteIds(values, existingIds) &&
          (!isEdit || isDirty);

        return (
          <>
            <Form.Header title={title} onClose={requestClose} closeLabel={t('COMMON_CLOSE')} />

            <Form.Section className={styles.body}>
              <ShippingSiteCards existingIds={existingIds} isEdit={isEdit} />
            </Form.Section>

            <Form.Footer>
              <Button variant={ButtonVariants.neutral} size={ButtonSizes.sm} onClick={requestClose}>
                {t('COMMON_CANCEL')}
              </Button>
              <Button
                type="submit"
                variant={ButtonVariants.primary}
                size={ButtonSizes.sm}
                disabled={isSubmitting || !canSave}
                loading={isSubmitting}
              >
                {t('COMMON_SAVE')}
              </Button>
            </Form.Footer>

            <ConfirmDialog
              open={confirmingClose}
              title={t('COMMON_DISCARD_TITLE')}
              bodyTitle={t('COMMON_DISCARD_BODY_TITLE')}
              description={t('COMMON_DISCARD_DESCRIPTION')}
              confirmLabel={t('COMMON_YES')}
              onConfirm={onClose}
              onCancel={keepEditing}
            />
          </>
        );
      }}
    </Form>
  );
};
